<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import subscriptionsHttpService, {
  type SubscriptionRow,
} from '@/services/http/subscriptionsHttpService.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t } = useI18n()

const query = useQuery({
  queryKey: ['admin', 'subscriptions'],
  queryFn: () => subscriptionsHttpService.list(),
})

const items = computed<SubscriptionRow[]>(() => query.data.value?.items ?? [])

// Дата в единый формат dd.mm.yyyy hh:mm (как на остальных админ-журналах).
function formatDate(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex flex-wrap items-center gap-3 w-full">
      <h1 class="text-xl font-bold mr-auto">{{ t('subscriptions.title') }}</h1>
      <span class="text-sm text-surface-500" data-test="subscriptions-count">
        {{ t('subscriptions.count', { n: items.length }) }}
      </span>
    </div>

    <DataTable
      :value="items"
      data-key="id"
      size="small"
      class="w-full"
      :loading="query.isLoading.value"
      data-test="subscriptions-table"
    >
      <template #empty>
        <span class="text-surface-500">{{ t('subscriptions.empty') }}</span>
      </template>
      <Column :header="t('subscriptions.columns.email')">
        <template #body="{ data }">{{ data.email ?? '—' }}</template>
      </Column>
      <Column :header="t('subscriptions.columns.phone')">
        <template #body="{ data }">{{ data.phone ?? '—' }}</template>
      </Column>
      <Column :header="t('subscriptions.columns.locale')">
        <template #body="{ data }">{{ data.locale ?? '—' }}</template>
      </Column>
      <Column field="eventSlug" :header="t('subscriptions.columns.event')" />
      <Column :header="t('subscriptions.columns.createdAt')">
        <template #body="{ data }">{{ formatDate(data.createdAt) }}</template>
      </Column>
    </DataTable>
  </section>
</template>
